import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { toDisplayAmount, formatMoney } from '@/lib/performance/money';
import { platformsInScope, type Platform, type PlatformScope } from '@/lib/performance/types';
import { PLATFORM_LABEL, PLATFORM_COLOR, PlatformName } from '@/lib/performance/platform';
import type { PerformanceData, MetaTimeSeriesRow } from '@/lib/types';
import { cn } from '@/lib/utils';

type MetricKey = 'spend' | 'results' | 'clicks' | 'ctr';

const METRICS: { key: MetricKey; label: string }[] = [
  { key: 'spend', label: 'Spend' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'results', label: 'Conversions' },
  { key: 'ctr', label: 'CTR' },
];

// One normalised point shape for both platforms so grouping (#6276) can sum
// components and re-derive CTR per bucket instead of averaging row ratios.
interface NormPoint {
  date: string;
  spend: number;
  clicks: number;
  results: number;
  impressions: number;
}

function normGoogle(r: PerformanceData): NormPoint {
  return { date: r.date, spend: r.cost ?? 0, clicks: r.clicks ?? 0, results: r.conversions ?? 0, impressions: r.impressions ?? 0 };
}

function normMeta(r: MetaTimeSeriesRow): NormPoint {
  return {
    date: r.day,
    spend: toDisplayAmount(r.spend_cents, 'meta'),
    clicks: r.clicks ?? 0,
    results: r.leads ?? 0,
    impressions: r.impressions ?? 0,
  };
}

// Same bucketing rule as the hook's chartData: ISO Monday for weeks, first of
// the month for months.
function bucketKey(iso: string, groupBy: 'day' | 'week' | 'month'): string {
  if (groupBy === 'day') return iso;
  const d = new Date(iso);
  if (groupBy === 'week') {
    const w = new Date(d);
    w.setDate(w.getDate() - ((w.getDay() + 6) % 7));
    return w.toISOString().split('T')[0];
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function aggregate(points: NormPoint[], groupBy: 'day' | 'week' | 'month'): NormPoint[] {
  if (groupBy === 'day') return [...points].sort((a, b) => a.date.localeCompare(b.date));
  const buckets = new Map<string, NormPoint>();
  for (const p of points) {
    const key = bucketKey(p.date, groupBy);
    const b = buckets.get(key) ?? { date: key, spend: 0, clicks: 0, results: 0, impressions: 0 };
    b.spend += p.spend;
    b.clicks += p.clicks;
    b.results += p.results;
    b.impressions += p.impressions;
    buckets.set(key, b);
  }
  return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function metricValue(p: NormPoint, metric: MetricKey): number {
  switch (metric) {
    case 'spend':
      return p.spend;
    case 'results':
      return p.results;
    case 'clicks':
      return p.clicks;
    case 'ctr':
      return p.impressions > 0 ? (p.clicks / p.impressions) * 100 : 0;
  }
}

// Exported for direct unit coverage (#6282) — chart tick/tooltip formatters
// never execute under happy-dom's zero-size ResponsiveContainer.
export function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface Props {
  scope: PlatformScope;
  googleRows: PerformanceData[] | undefined;
  metaRows: MetaTimeSeriesRow[] | undefined;
  currencySymbol: string;
  /** Toolbar grouping (#6276) — buckets both platforms' series before charting. */
  groupBy: 'day' | 'week' | 'month';
}

/**
 * Consolidated performance-over-time chart (#5971, CP-6). One metric at a time;
 * the platform toggle picks Google, Meta, or All (both — shown when the scope
 * allows). "All" draws one line per platform in that platform's colour (Google
 * green, Meta blue); Results stays split (Google Conversions vs Meta Leads),
 * never equated on the axis — the no-false-equivalence rule.
 */
export function PerformanceOverTime({ scope, googleRows, metaRows, currencySymbol, groupBy }: Props) {
  const platforms = platformsInScope(scope);
  const [platformChoice, setPlatformChoice] = useState<Platform | 'all'>('google');
  const [metric, setMetric] = useState<MetricKey>('spend');

  // 'all' only applies when both platforms are in scope; otherwise fall back to
  // the single in-scope platform.
  const canShowAll = platforms.length > 1;
  const choice: Platform | 'all' =
    platformChoice === 'all'
      ? canShowAll
        ? 'all'
        : platforms[0]
      : platforms.includes(platformChoice)
        ? platformChoice
        : platforms[0];

  // One row shape for both modes: recharts 3.9's ChartData generic rejects a
  // union of the 'all' rows ({google, meta}) and single-platform rows ({value}).
  // Each platform's series is bucketed by the toolbar grouping first (#6276).
  const data = useMemo<{ date: string; google?: number | null; meta?: number | null; value?: number }[]>(() => {
    const google = aggregate((googleRows ?? []).map(normGoogle), groupBy);
    const meta = aggregate((metaRows ?? []).map(normMeta), groupBy);
    if (choice === 'all') {
      const byDate = new Map<string, { date: string; google: number | null; meta: number | null }>();
      for (const p of google) {
        const e = byDate.get(p.date) ?? { date: p.date, google: null, meta: null };
        e.google = metricValue(p, metric);
        byDate.set(p.date, e);
      }
      for (const p of meta) {
        const e = byDate.get(p.date) ?? { date: p.date, google: null, meta: null };
        e.meta = metricValue(p, metric);
        byDate.set(p.date, e);
      }
      return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
    }
    const series = choice === 'google' ? google : meta;
    return series.map((p) => ({ date: p.date, value: metricValue(p, metric) }));
  }, [choice, googleRows, metaRows, metric, groupBy]);

  const isMoney = metric === 'spend';
  const isPct = metric === 'ctr';
  const format = (v: number) =>
    isMoney ? formatMoney(v, currencySymbol) : isPct ? `${v.toFixed(2)}%` : Math.round(v).toLocaleString();
  // Results is split by platform (Google Conversions vs Meta Leads); other
  // metrics share a label.
  const seriesName = (p: Platform) =>
    metric === 'results' ? (p === 'google' ? 'Conversions' : 'Leads') : METRICS.find((m) => m.key === metric)!.label;

  const platformOptions: { value: Platform | 'all'; label: ReactNode }[] = [
    { value: 'all', label: 'All' },
    ...platforms.map((p) => ({ value: p as Platform | 'all', label: <PlatformName platform={p} /> })),
  ];

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">Performance over time</p>
        <div className="flex items-center gap-2">
          {canShowAll && <SegToggle options={platformOptions} value={choice} onChange={setPlatformChoice} />}
          <SegToggle options={METRICS.map((m) => ({ value: m.key, label: m.label }))} value={metric} onChange={setMetric} />
        </div>
      </div>
      {data.length === 0 ? (
        <div className="flex h-56 items-center justify-center text-sm text-gray-400">
          No {choice === 'all' ? '' : `${PLATFORM_LABEL[choice]} `}data for this period
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={32} />
            <YAxis tickFormatter={format} tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={64} />
            <Tooltip
              formatter={(v, name) => [format(Number(v)), name]}
              labelFormatter={(l) => shortDate(String(l))}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
            />
            {choice === 'all' ? (
              <>
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="google" name={`Google ${seriesName('google')}`} stroke={PLATFORM_COLOR.google} strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="meta" name={`Meta ${seriesName('meta')}`} stroke={PLATFORM_COLOR.meta} strokeWidth={2} dot={false} connectNulls />
              </>
            ) : (
              <Line type="monotone" dataKey="value" name={seriesName(choice)} stroke={PLATFORM_COLOR[choice]} strokeWidth={2} dot={false} />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function SegToggle<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'h-7 rounded px-2.5 text-xs font-medium transition-colors',
            value === o.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
